<template>
    <div class="selected-time-slots">
        <div class="section-header">
            <h5 class="title">{{ $t('modules.time_blocks') }}</h5>
        </div>
        <design-2021-apprentice-data-table
            class="module-dates-table"
            :fields="fields"
            :items="moduleDates"
            nosearch
            stacked="md">
            <template v-slot:head(duration)="data">
                <div class="duration">{{ $t('modules.hours') }} <span class="required">*</span></div>
            </template>
            <template v-slot:head(modulePeriod)="data">
                <div class="duration">{{ $t('modules.time_block') }} <span class="required">*</span></div>
            </template>
            <template v-slot:head(max)="data">
                <div class="duration">{{ $t('common.city') }} <span class="required">*</span></div>
            </template>
            <template #cell(modulePeriod)="data">
                <div class="modulePeriod">
                    <design-2021-apprentice-multiselect-component
                        class="module-period-multi-select"
                        v-model="data.item.modulePeriod"
                        :id="`module-period-input-${data.index}`"
                        :multiple-select="false"
                        label="name"
                        track-by="id"
                        :options="modulePeriods"
                        @input="updateModuleDates">
                    </design-2021-apprentice-multiselect-component>
                </div>
            </template>
            <template #cell(start_end_time)="data">
                <div class="time_start">
                    <design-2021-apprentice-timepicker-component
                        v-model="data.item.time_start"
                        :id="`module-time-start-input-${data.index}`">
                    </design-2021-apprentice-timepicker-component>
                </div>
                <div class="time-end">
                    <design-2021-apprentice-timepicker-component
                        v-model="data.item.time_end"
                        :id="`module-time-end-input-${data.index}`">
                    </design-2021-apprentice-timepicker-component>
                </div>
            </template>

            <template #cell(signup_from_date)="data">
                <div class="sign-up-from-date">
                    <b-form-datepicker
                        class="apprentice-date-picker btn-date-group"
                        v-model="data.item.sign_up_from_date"
                        today-button
                        reset-button
                        right
                        :id="`module-sign-up-from-date-input-${data.index}`"
                        locale="nl"
                        start-weekday="1"
                        today-variant="primary"
                        nav-button-variant="primary"
                        :aria-controls="$t('modules.choose_start_date')"
                        label-reset-button="reset"
                        :label-no-date-selected="$t('modules.register_from')"
                        :label-current-month="$t('modules.this_month')"
                        :label-today-button="$t('modules.today')"
                        menu-class="datedropdown"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                        @context="updateModuleDates">
                    </b-form-datepicker>
                </div>
                <div class="sign-up-until-date">
                    <b-form-datepicker
                        class="apprentice-date-picker btn-date-group"
                        v-model="data.item.sign_up_until_date"
                        today-button
                        reset-button
                        right
                        :id="`module-sign-up-until-date-input-${data.index}`"
                        locale="nl"
                        start-weekday="1"
                        today-variant="primary"
                        nav-button-variant="primary"
                        :aria-controls="$t('modules.choose_end_date')"
                        label-reset-button="reset"
                        :label-no-date-selected="$t('modules.register_until')"
                        :label-current-month="$t('modules.this_month')"
                        :label-today-button="$t('modules.today')"
                        menu-class="datedropdown"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                        @context="updateModuleDates">
                    </b-form-datepicker>
                </div>
            </template>

            <template #cell(max)="data">
                <div class="seats">
                    <b-form-input
                        v-model="data.item.max"
                        :placeholder="$t('modules.max_students')"
                        type="number"
                        @change="updateModuleDates"></b-form-input>
                </div>
            </template>
            <template #cell(actions)="data">
                <div class="module-period-management">
                    <b-button
                        class="btn-remove btn-remove-time"
                        v-if="moduleDates.length > 1"
                        variant="danger-icon-only"
                        @click="removeDateItem(data.index)">
                    </b-button>
                </div>
            </template>
        </design-2021-apprentice-data-table>

        <div class="add-time">
            <b-button
                class="btn-add btn-add-time"
                variant="primary"
                @click="addTime">
                {{ $t('modules.add_time_block') }}
            </b-button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-module-index-availability-form-component',
    props: {
        moduleDatesIn: {
            type: Array,
            default: function () {
                return [];
            },
        },
        modulePeriodsIn: {
            type: Array,
        },
    },
    data() {
        return {
            moduleDates: this.moduleDatesIn.map((moduleDate) => {
                let modulePeriod = moduleDate.module_period ? moduleDate.module_period : null;

                if (modulePeriod) {
                    modulePeriod.title = modulePeriod.period.title;
                }
                return {
                    id: moduleDate.id,
                    modulePeriod: moduleDate.module_period ? moduleDate.module_period : null,
                    max: moduleDate.max,
                };
            }),
            modulePeriods: this.modulePeriodsIn.map((modulePeriod) => {
                modulePeriod.title = modulePeriod.period.title;
                return modulePeriod;
            }),
            weekdays: [
                this.$t('modules.monday'),
                this.$t('modules.tuesday'),
                this.$t('modules.wednesday'),
                this.$t('modules.thursday'),
                this.$t('modules..friday'),
                this.$t('modules.saturday'),
                this.$t('modules.sunday'),
            ],
            timeConfig: {
                format: 'H:mm',
                useCurrent: false,
                labelCloseButton: this.$t('common.close'),
                noCloseButton: true,
                icons: {
                    close: 'fa fa-times-circle',
                },
            },
            fields: [
                {
                    key: 'modulePeriod',
                    label: this.$t('modules.period'),
                },
                {
                    key: 'max',
                    label: this.$t('common.city'),
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                },
            ],
        };
    },
    methods: {
        addTime() {
            this.moduleDates.push({
                modulePeriod: null,
                max: null,
            });
        },

        removeDateItem(key) {
            this.moduleDates.splice(key, 1);
            this.$emit('change', this.moduleDates);
        },

        updateModuleDates() {
            this.$emit('change', this.moduleDates);
        },
    },
};
</script>

<style scoped lang="scss">
.section-header {
    border-bottom: 2px solid $border-color;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
}

.title {
    font-size: 1.125rem;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.module-dates-table ::v-deep {
    padding: 0;

    .apprentice-table-header {
        display: none;
    }

    tbody {
        tr {
            td {
                vertical-align: top;
            }
        }
    }

    .dateddropdown {
        padding-top: map-get($grid-gutter-spacing, 'ten');
        padding-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .apprentice-date-picker,
    .form-control {
        margin-bottom: 0;
    }

    .seats,
    .duration {
        > input {
            border: 1px solid $border-color;
        }
    }

    .module-period-multi-select {
        .multiselect__tags {
            height: 53px;
            border: 1px solid $border-color;

            input {
                border: none;
            }
        }
    }

    .module-period-management {
        height: 53px;
        display: flex;
        align-items: center;
    }

    .start-date,
    .sign-up-from-date,
    .sign-up-until-date,
    .end-date {
        .dropdown {
            border: 1px solid $border-color;
            border-radius: $border-radius-content;
        }
    }

    .start-date,
    .sign-up-from-date {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .start-date,
    .end-date {
        min-width: 140px;
    }

    .sign-up-from-date,
    .sign-up-until-date {
        min-width: 170px;
    }
}

.add-time {
    display: flex;
    justify-content: flex-end;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .module-dates-table ::v-deep {
        .table-responsive {
            overflow: initial;
        }
    }
}
</style>
